/*******************************
 * This component uses jotai async derived atoms, 
 * so it requires suspense wrapper around it when used
/*******************************/
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { currentChartTypeAtom } from 'views/state';

import { CAType } from './config';
import { getCASourceType } from './helper';
import {
  vulnBySeverityChartDataAtom,
  caCurrentVulnCountAtom,
  severityChartLabelsAtom,
  obsoleteVulnGroupsChartDataAtom,
  patchableVulnBySeverityChartDataAtom,
  vulnBySeverityChartDataCountAtom,
  patchableVulnBySeverityChartDataCountAtom,
  vulnGroupsChartDataAtom,
  vulnByStatusChartDataAtom,
  statusChartLabelsAtom,
  severityFiltersAtom,
  caTypeAtom,
  appliedCAIdsAtom,
  dashboardDataAtom,
  isObsoleteGroupingVulnListingOpenAtom,
  isGroupingByQidVulnListingOpenAtom,
} from './state';
import BarOrDoughnutChart from '../components/BarOrDoughnutChart';
import GroupingChart from '../components/GroupingChart';
import {
  getDrillDownUrl,
  getVulnerabilityStatusValue,
  getSeverityValue,
  getSeverityFilterValues,
  checkNonZeroArray,
  checkNonZeroObj,
} from '../helper';
import { Severity, VulnerabilityStatus } from '../types';

import '../styles.css';

type Props = {
  setModalComponentData: (ModalDialogData) => void;
};

const CurrentCharts = ({ setModalComponentData }: Props) => {
  const caType = useAtomValue(caTypeAtom);
  const currentChartType = useAtomValue(currentChartTypeAtom);
  const severityFilters = useAtomValue(severityFiltersAtom);
  const setCurrentVulnCount = useSetAtom(caCurrentVulnCountAtom);

  const vulnBySeverityChartData = useAtomValue(vulnBySeverityChartDataAtom);
  const severityChartLabels = useAtomValue(severityChartLabelsAtom);
  const obsoleteVulnGroupsChartData = useAtomValue(
    obsoleteVulnGroupsChartDataAtom,
  );
  const patchableVulnBySeverityChartData = useAtomValue(
    patchableVulnBySeverityChartDataAtom,
  );
  const vulnBySeverityChartDataCount = useAtomValue(
    vulnBySeverityChartDataCountAtom,
  );
  const patchableVulnBySeverityChartDataCount = useAtomValue(
    patchableVulnBySeverityChartDataCountAtom,
  );
  const vulnGroupsChartData = useAtomValue(vulnGroupsChartDataAtom);
  const vulnByStatusChartData = useAtomValue(vulnByStatusChartDataAtom);
  const statusChartLabels = useAtomValue(statusChartLabelsAtom);
  const selectedCAIds = useAtomValue(appliedCAIdsAtom);
  const dashboardData = useAtomValue(dashboardDataAtom);

  // grouping charts vulnerabilities listing dialog open/close state
  const [
    isObsoleteGroupingVulnListingOpen,
    setIsObsoleteGroupingVulnListingOpen,
  ] = useAtom(isObsoleteGroupingVulnListingOpenAtom);
  const [isGroupingByQidVulnListingOpen, setIsGroupingByQidVulnListingOpen] =
    useAtom(isGroupingByQidVulnListingOpenAtom);

  function getChartTooltipLabels(sourceData, caType: CAType) {
    const toolTipLabels: string[] = [];
    const server_values = Object.values<number>(sourceData[CAType.Server]);
    const workstation_values = Object.values<number>(
      sourceData[CAType.Workstation],
    );
    if (
      (caType === CAType.Server || caType === CAType.All) &&
      checkNonZeroArray(server_values)
    ) {
      toolTipLabels.push('Servers');
    }

    if (
      (caType === CAType.Workstation || caType === CAType.All) &&
      checkNonZeroArray(workstation_values)
    ) {
      toolTipLabels.push(CAType.Workstation);
    }

    return toolTipLabels;
  }

  function getVulnByStatusChartTooltipLabels(data, caType: CAType) {
    const toolTipLabels: string[] = [];
    const server_values = Object.values<number>(data[CAType.Server]);
    const workstation_values = Object.values<number>(data[CAType.Workstation]);
    if (
      (caType === CAType.Server || caType === CAType.All) &&
      checkNonZeroObj(server_values)
    ) {
      toolTipLabels.push('Servers');
    }

    if (
      (caType === CAType.Workstation || caType === CAType.All) &&
      checkNonZeroObj(workstation_values)
    ) {
      toolTipLabels.push(CAType.Workstation);
    }

    return toolTipLabels;
  }

  function getSeverityChartsDrillDownUrl(
    label: string,
    sourceType: string,
    patchable = false,
    obsolete = false,
  ) {
    return getDrillDownUrl({
      type: 'open',
      severity: getSeverityValue(Severity[label]).toString(),
      sourceType: getCASourceType(sourceType as CAType),
      issueType: 'Vulnerability',
      qid: '',
      patchable: patchable,
      obsolete: obsolete,
      severity_in: undefined,
      hosts_in: selectedCAIds,
    });
  }

  function getStatusChartsDrillDownUrl(status: string, sourceType: string) {
    // handle statuses, as the labels are customized
    if (status.toLocaleLowerCase().startsWith('new')) {
      status = VulnerabilityStatus.New;
    } else if (status.toLocaleLowerCase().startsWith('reopened')) {
      status = VulnerabilityStatus.Reopened;
    } else if (status.toLocaleLowerCase().startsWith('active')) {
      status = VulnerabilityStatus.Active;
    } else if (status.toLocaleLowerCase().startsWith('fixed')) {
      status = VulnerabilityStatus.Fixed;
    } else if (status.toLocaleLowerCase().startsWith('ignored')) {
      status = VulnerabilityStatus.Ignored;
    }

    return getDrillDownUrl({
      type: getVulnerabilityStatusValue(status as VulnerabilityStatus),
      severity: '',
      sourceType: getCASourceType(sourceType as CAType),
      issueType: 'Vulnerability',
      qid: '',
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedCAIds,
    });
  }

  function getGroupingDrillDownUrl(qid: string, sourceType: string) {
    return getDrillDownUrl({
      type: 'open',
      severity: '',
      sourceType: sourceType,
      issueType: 'Vulnerability',
      qid: qid,
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedCAIds,
    });
  }

  function getObsoleteGroupingDrillDownUrl(qid: string, sourceType: string) {
    return getDrillDownUrl({
      type: 'open',
      severity: '',
      sourceType: sourceType,
      issueType: 'Vulnerability',
      qid: qid,
      severity_in: undefined,
      hosts_in: selectedCAIds,
    });
  }

  // console.log({
  //     msg: 'tooltip labels',
  //     severityChartLabels: severityChartLabels,
  //     vulns_by_severity_toolTipLabels: getChartTooltipLabels(dashboardData.vulns_by_severity, caType),
  //     patchable_vulns_by_severity: getChartTooltipLabels(dashboardData.patchable_vulns_by_severity, caType)
  // })

  function onObsoleteChartExpandClick(ModalDialogData) {
    setIsObsoleteGroupingVulnListingOpen(true);
    setModalComponentData(ModalDialogData);
  }

  function onGroupingByQidChartExpandClick(ModalDialogData) {
    if (ModalDialogData) setIsGroupingByQidVulnListingOpen(true);
    setModalComponentData(ModalDialogData);
  }

  return (
    <>
      {vulnBySeverityChartData && (
        <>
          <div className="col-md-4">
            <BarOrDoughnutChart
              chartType={currentChartType}
              title="By Severity"
              labels={severityChartLabels}
              tooltipLabels={getChartTooltipLabels(
                dashboardData.vulns_by_severity,
                caType,
              )}
              datasets={vulnBySeverityChartData}
              severityFilters={severityFilters}
              total_count={vulnBySeverityChartDataCount}
              getDrillDownUrl={getSeverityChartsDrillDownUrl}
              setCurrentVulnCount={setCurrentVulnCount}
            />
          </div>
        </>
      )}
      {patchableVulnBySeverityChartData && (
        <>
          <div className="col-md-4">
            <BarOrDoughnutChart
              chartType={currentChartType}
              title="Patchable"
              labels={severityChartLabels}
              tooltipLabels={getChartTooltipLabels(
                dashboardData.patchable_vulns_by_severity,
                caType,
              )}
              datasets={patchableVulnBySeverityChartData}
              severityFilters={severityFilters}
              total_count={patchableVulnBySeverityChartDataCount}
              getDrillDownUrl={(severity: string, sourceType: string) =>
                getSeverityChartsDrillDownUrl(severity, sourceType, true, false)
              }
            />
          </div>
        </>
      )}
      {vulnByStatusChartData && (
        <div className="col-md-4">
          <BarOrDoughnutChart
            chartType={currentChartType}
            title="By Status"
            labels={statusChartLabels}
            tooltipLabels={getVulnByStatusChartTooltipLabels(
              dashboardData.vulns_by_status,
              caType,
            )}
            datasets={vulnByStatusChartData}
            severityFilters={Object.values(VulnerabilityStatus)}
            getDrillDownUrl={getStatusChartsDrillDownUrl}
          />
        </div>
      )}
      {obsoleteVulnGroupsChartData && (
        <>
          {
            <div className="col-md-6">
              <GroupingChart
                chartType={currentChartType}
                title="Obsolete Vulnerability Groups"
                data={obsoleteVulnGroupsChartData}
                onExpandClick={(ModalDialogData) => {
                  onObsoleteChartExpandClick(ModalDialogData);
                }}
                severityFilters={severityFilters}
                sourceType={getCASourceType(caType)}
                getDrillDownUrl={getObsoleteGroupingDrillDownUrl}
                isVulnListingOpen={isObsoleteGroupingVulnListingOpen}
              />
            </div>
          }
        </>
      )}
      {vulnGroupsChartData && (
        <>
          {
            <div className="col-md-6">
              <GroupingChart
                chartType={currentChartType}
                title="Group by QIDs"
                data={vulnGroupsChartData}
                onExpandClick={(ModalDialogData) => {
                  onGroupingByQidChartExpandClick(ModalDialogData);
                }}
                severityFilters={severityFilters}
                sourceType={getCASourceType(caType)}
                getDrillDownUrl={getGroupingDrillDownUrl}
                isVulnListingOpen={isGroupingByQidVulnListingOpen}
              />
            </div>
          }
        </>
      )}
    </>
  );
};

export default CurrentCharts;
